const PREVIEW_TOKEN_STORAGE_KEY = 'netuno-preview-token';
const PREVIEW_TOKEN_LOCAL_STORAGE_KEY = 'netuno-preview-token-local';
const PREVIEW_HISTORY_GUARD_FLAG = '__NETUNO_PREVIEW_HISTORY_GUARD_ACTIVE__';
let inMemoryPreviewToken: string | null = null;
export const PREVIEW_TOKEN_QUERY_PARAM = '__lovable_token';

export function isLovablePreviewEnvironment(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname.toLowerCase();

  return (
    hostname.startsWith('id-preview--') ||
    hostname.endsWith('.lovableproject.com') ||
    hostname.endsWith('.lovable.app')
  );
}

export function getPersistedPreviewToken(): string | null {
  if (typeof window === 'undefined') return inMemoryPreviewToken;

  try {
    const storageToken = sessionStorage.getItem(PREVIEW_TOKEN_STORAGE_KEY);
    if (storageToken) {
      inMemoryPreviewToken = storageToken;
      return storageToken;
    }
  } catch {
    // no-op: fallback to persistent storage
  }

  try {
    const persistentToken = localStorage.getItem(PREVIEW_TOKEN_LOCAL_STORAGE_KEY);
    if (persistentToken) {
      inMemoryPreviewToken = persistentToken;
      try {
        sessionStorage.setItem(PREVIEW_TOKEN_STORAGE_KEY, persistentToken);
      } catch {
        // Storage may be unavailable
      }
      return persistentToken;
    }
  } catch {
    // no-op: fallback to in-memory token
  }

  return inMemoryPreviewToken;
}

function extractPreviewTokenFromContext(): string | null {
  if (typeof window === 'undefined') return null;

  const tryReadToken = (raw: string | null | undefined) => {
    if (!raw) return null;
    try {
      const url = new URL(raw, window.location.origin);
      return url.searchParams.get(PREVIEW_TOKEN_QUERY_PARAM);
    } catch {
      return null;
    }
  };

  const fromReferrer = tryReadToken(document.referrer);
  if (fromReferrer) return fromReferrer;

  try {
    const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    for (const entry of navigationEntries) {
      const token = tryReadToken(entry.name);
      if (token) return token;
    }
  } catch {
    // Performance API may be unavailable
  }

  return null;
}

export function persistPreviewTokenFromUrl(search: string = window.location.search): string | null {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(search);
  const token = params.get(PREVIEW_TOKEN_QUERY_PARAM) ?? extractPreviewTokenFromContext();

  if (!token) return getPersistedPreviewToken();

  inMemoryPreviewToken = token;

  try {
    sessionStorage.setItem(PREVIEW_TOKEN_STORAGE_KEY, token);
  } catch {
    // Storage may be unavailable in some browser/privacy contexts
  }

  try {
    localStorage.setItem(PREVIEW_TOKEN_LOCAL_STORAGE_KEY, token);
  } catch {
    // Storage may be unavailable in some browser/privacy contexts
  }

  return token;
}

export function ensurePreviewTokenInCurrentUrl(): void {
  if (typeof window === 'undefined' || !isLovablePreviewEnvironment()) return;

  const token = persistPreviewTokenFromUrl();
  if (!token) return;

  const params = new URLSearchParams(window.location.search);
  if (params.get(PREVIEW_TOKEN_QUERY_PARAM) === token) return;

  params.set(PREVIEW_TOKEN_QUERY_PARAM, token);
  const nextUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
  window.history.replaceState(window.history.state, '', nextUrl);
}

/**
 * Ensures preview token is present BEFORE bootstrap-sensitive flows.
 * If the current URL is missing the token but one is recoverable from
 * session/referrer/navigation context, forces a hard reload with tokenized URL.
 */
export function enforcePreviewTokenOnInitialLoad(): boolean {
  if (typeof window === 'undefined' || !isLovablePreviewEnvironment()) return false;

  const params = new URLSearchParams(window.location.search);
  if (params.get(PREVIEW_TOKEN_QUERY_PARAM)) {
    persistPreviewTokenFromUrl(window.location.search);
    return false;
  }

  const token = persistPreviewTokenFromUrl(window.location.search);
  if (!token) return false;

  params.set(PREVIEW_TOKEN_QUERY_PARAM, token);
  const nextUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;

  window.location.replace(nextUrl);
  return true;
}

function withPreviewToken(rawUrl: string | URL | null | undefined): string | URL | null | undefined {
  if (typeof window === 'undefined' || !isLovablePreviewEnvironment()) return rawUrl;
  if (!rawUrl) return rawUrl;

  const token = persistPreviewTokenFromUrl() ?? getPersistedPreviewToken();
  if (!token) return rawUrl;

  try {
    const url = new URL(rawUrl.toString(), window.location.origin);
    if (url.origin !== window.location.origin) return rawUrl;

    if (!url.searchParams.has(PREVIEW_TOKEN_QUERY_PARAM)) {
      url.searchParams.set(PREVIEW_TOKEN_QUERY_PARAM, token);
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return rawUrl;
  }
}

export function buildPathWithPreviewToken(path: string): string {
  const next = withPreviewToken(path);
  return typeof next === 'string' ? next : path;
}

export function installPreviewHistoryTokenGuard() {
  if (typeof window === 'undefined' || !isLovablePreviewEnvironment()) return () => undefined;
  if ((window as any)[PREVIEW_HISTORY_GUARD_FLAG]) return () => undefined;

  persistPreviewTokenFromUrl();

  const originalPushState = window.history.pushState.bind(window.history);
  const originalReplaceState = window.history.replaceState.bind(window.history);

  window.history.pushState = ((data, unused, url) => {
    originalPushState(data, unused, withPreviewToken(url) as string | URL | null | undefined);
  }) as History['pushState'];

  window.history.replaceState = ((data, unused, url) => {
    originalReplaceState(data, unused, withPreviewToken(url) as string | URL | null | undefined);
  }) as History['replaceState'];

  (window as any)[PREVIEW_HISTORY_GUARD_FLAG] = true;

  return () => {
    window.history.pushState = originalPushState;
    window.history.replaceState = originalReplaceState;
    (window as any)[PREVIEW_HISTORY_GUARD_FLAG] = false;
  };
}
